
import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addDays, format, isToday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

export interface DaySelectorProps {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

const DaySelector: React.FC<DaySelectorProps> = ({ selectedDate, setSelectedDate }) => {
  const handlePreviousDay = () => {
    setSelectedDate(addDays(selectedDate, -1));
  };

  const handleNextDay = () => {
    setSelectedDate(addDays(selectedDate, 1));
  };

  const today = new Date();
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(today, i - 3);
    return {
      date,
      isToday: isToday(date),
      isSelected: isSameDay(date, selectedDate),
      dayName: format(date, 'EEE', { locale: es }),
      dayNumber: format(date, 'd')
    };
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <Button 
          variant="outline" 
          onClick={handlePreviousDay}
          className="rounded-full w-8 h-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <h2 className="text-lg font-semibold">
          {format(selectedDate, 'EEEE, d MMMM', { locale: es })}
        </h2>
        
        <Button 
          variant="outline" 
          onClick={handleNextDay}
          className="rounded-full w-8 h-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="flex justify-center">
        <div className="grid grid-cols-7 gap-[2px] w-full">
          {daysOfWeek.map((day) => (
            <div
              key={day.date.toString()}
              className="flex flex-col items-center"
            >
              <span className="text-[10px] text-gray-500 mb-1">{day.dayName}</span>
              <Button
                variant={day.isSelected ? "default" : "ghost"}
                className={`h-8 w-8 rounded-full p-0 ${
                  day.isSelected ? "bg-green-600 hover:bg-green-700" : ""
                } ${
                  day.isToday && !day.isSelected ? "text-green-600" : ""
                }`}
                onClick={() => setSelectedDate(day.date)}
              >
                <span className="text-sm font-bold">{day.dayNumber}</span>
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DaySelector;
